/**
 * Warm the payments partner's card-form code before the form mounts.
 *
 * Why: the Elements card form is ~110 JS files (~600 KB compressed: the
 * checkout frame alone lists 83 chunks, 534 KB). A buyer's browser only
 * started downloading them when the form mounted, which waits for our own
 * payment session, and the partner's session request waits for that code.
 * Measured locally (2026-11-02, cold cache): mount → ready 4.2 s → 2.5 s with
 * the code warmed, 6.0 s → 4.5 s at 150 ms latency.
 *
 * How: the partner's frame pages list their chunks as `modulepreload` links
 * and the files are cached for a year (`immutable`). Loading the two frame
 * pages the form uses (the checkout controller and the checkout element, in
 * the buyer's locale) in hidden iframes puts that code in the browser cache
 * under the same partition the real frames use (this page + cdn.whop.com).
 * Without a `#host` the frames never boot or talk to the page: they only
 * download. The frame URLs are content-hashed, so they are read from the
 * manifest inside the partner's loader script, never hard-coded.
 *
 * Skipped for Save-Data and 2G buyers (it is a download they did not ask
 * for), runs once per page, and never throws.
 */

/** The partner's loader script (keep in step with `SDK_URL` in `@whop/elements`). */
export const PARTNER_SDK_URL =
	"https://cdn.whop.com/elements/amber/elements.js";

/** Frames are removed after this long; the downloads are done or cached by then. */
export const WARM_FRAME_LIFETIME_MS = 60_000;

const FALLBACK_LOCALE = "en";

/**
 * The frame pages to warm, as absolute URLs, from the loader's manifest
 * (`"checkout/checkout/en":`checkout/checkout/en/index-106B5A82.html``).
 * Empty when the manifest does not have them (a partner release changed it).
 */
export function partnerFrameUrls(
	sdkSource: string,
	locale: string | undefined,
	sdkUrl: string = PARTNER_SDK_URL,
): string[] {
	const base = sdkUrl.slice(0, sdkUrl.lastIndexOf("/") + 1);
	const lookup = (key: string): string | null => {
		const escaped = key.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");
		const match = new RegExp(
			`"${escaped}"\\s*:\\s*[\`"']([A-Za-z0-9_./-]+\\.html)[\`"']`,
		).exec(sdkSource);
		return match ? match[1] : null;
	};
	const element =
		(locale ? lookup(`checkout/checkout/${locale}`) : null) ??
		lookup(`checkout/checkout/${FALLBACK_LOCALE}`);
	const controller = lookup("checkout/_controller");
	return [controller, element]
		.filter((path): path is string => Boolean(path))
		.map((path) => base + path);
}

type NetworkInformationLike = {
	saveData?: boolean;
	effectiveType?: string;
};

/** Save-Data on, or a 2G connection: do not download what was not asked for. */
export function shouldSkipWarm(connection?: NetworkInformationLike | null) {
	if (!connection) return false;
	if (connection.saveData) return true;
	return (
		connection.effectiveType === "2g" || connection.effectiveType === "slow-2g"
	);
}

let started = false;

/** Test-only: allow another warm in the same module instance. */
export function resetPartnerFrameWarmForTests() {
	started = false;
}

export async function warmPartnerFrames({
	locale,
	doc = typeof document === "undefined" ? undefined : document,
	fetchImpl = typeof fetch === "undefined" ? undefined : fetch,
	connection = typeof navigator === "undefined"
		? undefined
		: (navigator as Navigator & { connection?: NetworkInformationLike })
				.connection,
}: {
	locale?: string;
	doc?: Document;
	fetchImpl?: typeof fetch;
	connection?: NetworkInformationLike | null;
} = {}): Promise<string[]> {
	if (started || !doc?.body || !fetchImpl) return [];
	// The early entry (src/checkoutEarly.ts) and the app may each carry a copy
	// of this module: the page itself says whether a warm-up already ran.
	if (doc.querySelector("[data-pf-partner-warm]")) return [];
	if (shouldSkipWarm(connection)) return [];
	started = true;
	try {
		// The loader script is already loaded (or preloaded) by now: this read
		// comes from the browser cache in the normal case.
		const response = await fetchImpl(PARTNER_SDK_URL, {
			mode: "cors",
			credentials: "omit",
			cache: "force-cache",
		});
		if (!response.ok) return [];
		const urls = partnerFrameUrls(await response.text(), locale);
		for (const url of urls) {
			const frame = doc.createElement("iframe");
			frame.src = url;
			frame.title = "";
			frame.tabIndex = -1;
			frame.setAttribute("aria-hidden", "true");
			frame.setAttribute("data-pf-partner-warm", "");
			frame.style.cssText =
				"position:absolute;width:0;height:0;border:0;visibility:hidden;pointer-events:none";
			doc.body.appendChild(frame);
			setTimeout(() => frame.remove(), WARM_FRAME_LIFETIME_MS);
		}
		return urls;
	} catch {
		return [];
	}
}
